import React, { FC, useCallback, useState, Fragment } from 'react'
import { useStoreActions, useStoreState } from '@mortgage-pos/ui/store'
import rollbar from '@mortgage-pos/ui/services/rollbar'
import Button, { ButtonVariants } from '@mortgage-pos/ui/components/Button'
import { Form, Formik } from 'formik'
import {
  useInitialValues,
  useValidate,
} from '@mortgage-pos/ui/utils/formikHelpers'
import { deriveAnswersFromFormValues } from '@mortgage-pos/ui/utils/pageHelpers'
import { useVariation } from '@mortgage-pos/ui/services/featureFlags'
import { FEATURE_FLAG_TYPES } from '@mortgage-pos/data'
import { useNavigate } from 'react-router-dom'
import { Routes } from '@mortgage-pos/types'
import SingleQuestion from '@mortgage-pos/ui/components/SingleQuestion'
import { updateRateInputs } from '@mortgage-pos/ui/data/updateRateInputs'

export interface updateRatesProps {
  refreshRates: () => Promise<void>
}

export const UpdateRates: FC<updateRatesProps> = ({ refreshRates }) => {
  const navigate = useNavigate()

  const { applicationId } = useStoreState((state) => state.application)
  const { mergedAnswers, maxCashoutValue } = useStoreState(
    (state) => state.answers
  )
  const prebuiltValidations = useStoreState(
    (state) => state.visibleIfConditions
  )

  const { answer } = useStoreActions((state) => state.questionnaire)

  const [formTouched, setFormTouched] = useState(false)

  const updateRateQuestions = updateRateInputs(maxCashoutValue)
  const validate = useValidate(updateRateQuestions)

  const handleChange = useCallback(() => {
    setFormTouched(true)
  }, [setFormTouched])

  const handleSubmitNewInputs = useCallback(
    async (values) => {
      try {
        const answers = deriveAnswersFromFormValues(
          updateRateInputs(maxCashoutValue),
          { ...mergedAnswers, ...values },
          prebuiltValidations
        )
        await answer(answers)
      } catch (e) {
        rollbar.error('updateRates: error while updating rates', e, {
          applicationId,
        })
        navigate(`/${Routes.APP_1003}/error`)
      }
      await refreshRates()
      setFormTouched(false)
    },
    [mergedAnswers, answer, applicationId, navigate, setFormTouched]
  )

  const initialValues = useInitialValues(updateRateQuestions)

  return (
    <Formik
      initialValues={initialValues}
      validate={validate}
      onSubmit={handleSubmitNewInputs}
    >
      {({ values, touched, handleSubmit }) => {
        return (
          <div className="update-rates">
            <Form
              className="update-rates__form"
              data-testid="update-rates-form"
            >
              {updateRateQuestions.map((question) => {
                return (
                  <Fragment key={question.questionName.name}>
                    <SingleQuestion
                      question={question}
                      onChange={handleChange}
                    />
                  </Fragment>
                )
              })}
              <div className="update-rates__button-container">
                <div className="update-rates__button-wrapper">
                  <Button
                    variant={ButtonVariants.next}
                    content={'Update Results'}
                    className="update-rates__submit-button"
                    data-testid="update-rates-submit-button"
                    type="submit"
                    disabled={!formTouched}
                    onClick={(e) => {
                      e.preventDefault()
                      handleSubmit(values)
                    }}
                  />
                </div>
              </div>
            </Form>
          </div>
        )
      }}
    </Formik>
  )
}

export default UpdateRates
